import { Slot } from '@radix-ui/react-slot'
import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import * as React from 'react'

import { ArrowRight } from '@/components/icons/ArrowRight'
import { Google as GoogleIcon } from '@/components/icons/social/Google'
import { cn } from '@/lib/styles'

import { Spinner } from '../elementary/Spinner'

const buttonVariants = cva(
	'inline-flex items-center justify-center disabled:pointer-events-none disabled:bg-wolf-gray-200 disabled:text-wolf-gray-500 disabled:opacity-100 transition-[color,background-color,border-color,box-shadow] duration-200 ease-in-out cursor-pointer',
	{
		variants: {
			size: {
				large: 'h-13 px-6 py-3 lok-text-body-bold-large',
				medium: 'h-11 px-5 py-2.5 lok-text-body-bold-medium',
				small: 'h-10.5 px-5 lok-text-body-bold-medium',
				pillow: 'px-3 py-0.75',
				switch: 'px-6 py-1.5',
				none: 'p-0',
			},
			variant: {
				primary:
					'rounded-button bg-wolf-gray-900 hover:bg-wolf-gray-700 active:bg-wolf-gray-800 text-wolf-gray-25 leading-1 text-center sm:text-left',
				secondary:
					'rounded-button border border-wolf-gray-500 outline-2 outline-transparent -outline-offset-2 transition-[color,background-color,border-color,outline-color] duration-200 text-wolf-gray-950 hover:border-wolf-gray-950 hover:outline-wolf-gray-500 active:border-wolf-gray-950 active:outline-wolf-gray-500 disabled:border-wolf-gray-300 disabled:text-wolf-gray-400 disabled:bg-transparent leading-1 text-center sm:text-left',
				google:
					'rounded-button overflow-hidden p-0 disabled:bg-transparent disabled:text-white disabled:opacity-100',
				pillowLarge:
					'rounded-full border border-neutral-light-alpha-500 hover:border-wolf-gray-900 bg-transparent text-wolf-gray-900 hover:text-wolf-gray-700 hover:bg-neutral-dark-alpha-500 active:bg-wolf-gray-900 active:text-wolf-gray-25 active:border-wolf-gray-900 lok-text-body-bold-medium',
				pillowSmall:
					'rounded-full border border-neutral-light-alpha-500 hover:border-wolf-gray-900 bg-transparent text-wolf-gray-900 hover:text-wolf-gray-700 hover:bg-neutral-dark-alpha-500 text-sm/2 lg:text-base/3 active:bg-wolf-gray-900 active:text-wolf-gray-25 active:border-wolf-gray-900',
				switch:
					'rounded-button border border-neutral-light-alpha-500 hover:border-wolf-gray-900 bg-transparent text-wolf-gray-900 hover:text-wolf-gray-700 hover:bg-neutral-dark-alpha-500 active:bg-wolf-gray-900 active:text-wolf-gray-25 active:border-wolf-gray-900 lok-text-body-bold-small',
				primaryLink:
					'text-brand-black rounded-none  font-bold text-lg/4 lg:text-3xl/11 focus-visible:text-brand-salmon hover:text-brand-salmon active:text-brand-salmon',
				secondaryLink:
					'text-neutral-dark-300  rounded-none font-bold lg:text-2xl/11 text-base/4 focus-visible:text-brand-salmon hover:text-brand-salmon active:text-brand-salmon',
				tertiaryLink:
					'text-neutral-dark-300 rounded-none font-normal underline text-2xl/9 lg:text-2xl/7 focus-visible:text-brand-salmon hover:text-brand-salmon active:text-brand-salmon',
				quaternaryLink:
					'text-neutral-dark-300 rounded-none font-normal underline text-lg/5 lg:text-lg/7 focus-visible:text-brand-salmon hover:text-brand-salmon active:text-brand-salmon',
				none: '',
				ghost: 'hover:text-brand-salmon duration-200 transition-colors',
			},
			inverted: {
				true: '',
				false: '',
			},
		},
		compoundVariants: [
			{
				variant: 'primary',
				inverted: true,
				className:
					'bg-wolf-gray-25 text-wolf-gray-950 hover:bg-wolf-gray-100 active:bg-wolf-gray-200 disabled:bg-wolf-gray-600 disabled:text-wolf-gray-400 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-wolf-gray-900',
			},
			{
				variant: 'secondary',
				inverted: true,
				className:
					'border-wolf-gray-25 bg-transparent text-wolf-gray-25 hover:bg-wolf-gray-800 active:bg-wolf-gray-700 disabled:border-wolf-gray-600 disabled:text-wolf-gray-500 disabled:bg-transparent',
			},
		],
		defaultVariants: {
			variant: 'primary',
			size: 'large',
			inverted: false,
		},
	},
)

interface ButtonBaseProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {}

interface ButtonSlotProps extends ButtonBaseProps {
	asChild: true
	isLoading?: never
	iconStart?: never
	iconEnd?: never
}

interface ButtonStandardProps extends ButtonBaseProps {
	asChild?: false
	isLoading?: boolean
	iconStart?: React.ReactNode
	iconEnd?: boolean
}

export type ButtonProps = ButtonSlotProps | ButtonStandardProps

export function OAuthSplitLayout({ children }: { children: React.ReactNode }) {
	return (
		<span className="flex items-center h-full w-full">
			<span className="flex items-center justify-center aspect-square h-full shrink-0 rounded-l-button bg-white border border-fjord-blue-600 group-hover/oauth:border-fjord-blue-700 group-active/oauth:border-fjord-blue-800 group-disabled/oauth:border-fjord-blue-300 transition-colors duration-200">
				<GoogleIcon className="size-6 group-disabled/oauth:opacity-50" />
			</span>
			<span className="flex flex-1 items-center justify-center rounded-r-button bg-fjord-blue-600 group-hover/oauth:bg-fjord-blue-700 group-active/oauth:bg-fjord-blue-800 group-disabled/oauth:bg-fjord-blue-300 px-6 h-full text-white lok-text-body-bold-large transition-colors duration-200">
				{children}
			</span>
		</span>
	)
}

function IconLayout({
	iconStart,
	iconEnd,
	size,
	children,
}: {
	iconStart?: React.ReactNode
	iconEnd?: boolean
	size: ButtonProps['size']
	children: React.ReactNode
}) {
	const isLarge = size === 'large'
	return (
		<>
			{iconStart && (
				<span className={cn('shrink-0 mr-2', isLarge ? 'size-6' : 'size-5')}>{iconStart}</span>
			)}
			{children}
			{iconEnd && (
				<span
					className={cn(
						'shrink-0 ml-1 transition-transform duration-200 group-hover/icon-end:translate-x-1',
						isLarge ? 'size-5' : 'size-4',
					)}
				>
					<ArrowRight className="size-full" />
				</span>
			)}
		</>
	)
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			inverted,
			asChild = false,
			isLoading,
			iconStart,
			iconEnd,
			disabled,
			...props
		},
		ref,
	) => {
		const Comp = asChild ? Slot : 'button'
		const isOAuth = variant === 'google'
		const isDisabled = disabled || isLoading
		const ariaProps = {
			...(isLoading && { 'aria-busy': true }),
			...(asChild && isDisabled && { 'aria-disabled': true }),
		}
		return (
			<Comp
				className={cn(
					buttonVariants({ variant, size, inverted, className }),
					isOAuth && 'group/oauth',
					iconEnd && 'group/icon-end',
				)}
				ref={ref}
				disabled={!asChild && isDisabled}
				{...ariaProps}
				{...props}
			>
				{isLoading && !isOAuth ? (
					<Spinner>{props.children}</Spinner>
				) : isOAuth ? (
					<OAuthSplitLayout>{props.children}</OAuthSplitLayout>
				) : iconStart || iconEnd ? (
					<IconLayout iconStart={iconStart} iconEnd={iconEnd} size={size}>
						{props.children}
					</IconLayout>
				) : (
					props.children
				)}
			</Comp>
		)
	},
)
Button.displayName = 'Button'

export { Button, buttonVariants }
